import { useCallback, useState } from "react";
import type { Track, TrackLayout } from "@vms/types";
import { Button } from "./Button.js";
import { LoadingSpinner } from "./LoadingSpinner.js";
import { Modal } from "./Modal.js";
import { NoContentNotice } from "./NoContentNotice.js";
import { SearchInput } from "./SearchInput.js";

/**
 * Per-item coverage info for cross-venue scopes. Key is the track's
 * `trackId` (without layout suffix). When provided AND the count is less
 * than `totalVenues`, the picker surfaces an amber "in N of M venues" hint.
 */
export interface TrackCoverageMap {
  totalVenues: number;
  byTrackId: Record<string, { availableInVenues: string[] }>;
}

interface TrackPickerProps {
  tracks: Track[] | null | undefined;
  value: string;
  onChange: (value: string) => void;
  loading?: boolean;
  disabled?: boolean;
  coverage?: TrackCoverageMap;
}

/**
 * Presentational single-track picker. Caller fetches `tracks` (typically
 * scoped by game) and passes them in. Emits a `trackId` or `trackId/layoutId`
 * string via `onChange`. Falls back to a free-text input if no tracks are
 * available — preserves the hub-ui behaviour where the content mirror may be
 * empty.
 */
export function TrackPicker({
  tracks,
  value,
  onChange,
  loading,
  disabled,
  coverage,
}: TrackPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [layoutPickTrack, setLayoutPickTrack] = useState<Track | null>(null);

  const hasTracks = tracks && tracks.length > 0;

  const handleSearch = useCallback((q: string) => setSearch(q), []);

  const filteredTracks = tracks?.filter((track) => {
    const q = search.toLowerCase();
    return (
      track.name.toLowerCase().includes(q) ||
      track.trackId.toLowerCase().includes(q) ||
      (track.country && track.country.toLowerCase().includes(q)) ||
      (track.city && track.city.toLowerCase().includes(q))
    );
  });

  function selectTrack(track: Track) {
    if (track.layouts && track.layouts.length > 0) {
      setLayoutPickTrack(track);
      return;
    }
    emitValue(track.trackId);
  }

  function selectLayout(track: Track, layout: TrackLayout) {
    emitValue(`${track.trackId}/${layout.layoutId}`);
    setLayoutPickTrack(null);
  }

  function emitValue(val: string) {
    onChange(val);
    setIsOpen(false);
    setSearch("");
  }

  function getSelectedInfo(
    val: string,
  ): { name: string; outlineUrl?: string; previewUrl?: string; lengthM?: number } | null {
    if (!tracks || !val) return null;
    const [trackId, layoutId] = val.split("/");
    const track = tracks.find((t) => t.trackId === trackId);
    if (!track) return null;
    if (layoutId && track.layouts) {
      const layout = track.layouts.find((l) => l.layoutId === layoutId);
      if (layout) {
        return {
          name: `${track.name} / ${layout.name}`,
          outlineUrl: layout.outlineUrl,
          previewUrl: layout.previewUrl ?? track.previewUrl,
          lengthM: layout.lengthM,
        };
      }
    }
    return {
      name: track.name,
      outlineUrl: track.outlineUrl,
      previewUrl: track.previewUrl,
      lengthM: track.lengthM,
    };
  }

  function coverageHintFor(trackId: string): string | null {
    if (!coverage) return null;
    const entry = coverage.byTrackId[trackId];
    if (!entry) return null;
    if (entry.availableInVenues.length >= coverage.totalVenues) return null;
    return `In ${entry.availableInVenues.length} of ${coverage.totalVenues} venues`;
  }

  if (!loading && !hasTracks) {
    return (
      <NoContentNotice
        message="No tracks uploaded yet. Upload track content to choose one."
        currentValue={value || undefined}
      />
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(true)}
        className={`block w-full text-left rounded-lg border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary border-gray-300 ${
          disabled ? "bg-gray-50 cursor-not-allowed" : "bg-white cursor-pointer hover:bg-gray-50"
        }`}
      >
        {value ? (() => {
          const info = getSelectedInfo(value);
          if (!info) return <span className="text-gray-900">{value}</span>;
          const imageUrl = info.outlineUrl || info.previewUrl;
          return (
            <span className="flex items-center gap-3">
              {imageUrl && (
                info.outlineUrl ? (
                  <span className="inline-flex w-10 h-10 bg-gray-800 rounded flex-shrink-0 items-center justify-center p-0.5">
                    <img src={imageUrl} alt="" className="max-w-full max-h-full object-contain" />
                  </span>
                ) : (
                  <img src={imageUrl} alt="" className="w-10 h-10 object-cover rounded flex-shrink-0" />
                )
              )}
              <span>
                <span className="text-gray-900 block">{info.name}</span>
                {info.lengthM && (
                  <span className="text-xs text-gray-400">{(info.lengthM / 1000).toFixed(2)} km</span>
                )}
              </span>
            </span>
          );
        })() : (
          <span className="text-gray-400">Select a track...</span>
        )}
      </button>

      {layoutPickTrack ? (
        <Modal
          isOpen={isOpen}
          onClose={() => { setIsOpen(false); setSearch(""); setLayoutPickTrack(null); }}
          title={`${layoutPickTrack.name} - Select Layout`}
        >
          <div className="mb-4">
            <Button type="button" variant="secondary" size="sm" onClick={() => setLayoutPickTrack(null)}>
              Back to tracks
            </Button>
          </div>
          <div className="max-h-80 overflow-y-auto space-y-2">
            {layoutPickTrack.layouts?.map((layout) => (
              <button
                key={layout.id}
                type="button"
                onClick={() => selectLayout(layoutPickTrack, layout)}
                className="w-full text-left flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50"
              >
                {layout.outlineUrl ? (
                  <div className="w-14 h-14 bg-gray-800 rounded flex-shrink-0 flex items-center justify-center p-1">
                    <img src={layout.outlineUrl} alt="" className="max-w-full max-h-full object-contain" />
                  </div>
                ) : layout.previewUrl ? (
                  <img
                    src={layout.previewUrl}
                    alt=""
                    className="w-14 h-14 object-cover rounded flex-shrink-0"
                  />
                ) : (
                  <div className="w-14 h-14 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                    <svg className="h-5 w-5 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{layout.name}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {[
                      layout.year,
                      layout.type,
                      layout.lengthM ? `${(layout.lengthM / 1000).toFixed(2)} km` : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </Modal>
      ) : (
        <Modal
          isOpen={isOpen}
          onClose={() => { setIsOpen(false); setSearch(""); }}
          title="Select Track"
        >
          <div className="mb-4">
            <SearchInput onSearch={handleSearch} placeholder="Search tracks..." />
          </div>
          {loading ? (
            <LoadingSpinner />
          ) : (filteredTracks?.length ?? 0) === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">
              {search ? "No tracks match your search." : "No tracks available."}
            </p>
          ) : (
            <div className="max-h-80 overflow-y-auto space-y-2">
              {filteredTracks?.map((track) => {
                const hint = coverageHintFor(track.trackId);
                return (
                  <button
                    key={track.id}
                    type="button"
                    onClick={() => selectTrack(track)}
                    className="w-full text-left flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50"
                  >
                    {track.previewUrl ? (
                      <img
                        src={track.previewUrl}
                        alt=""
                        className="w-16 h-10 object-cover rounded flex-shrink-0"
                      />
                    ) : (
                      <div className="w-16 h-10 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                        <svg className="h-5 w-5 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{track.name}</p>
                      <p className="text-xs text-gray-500">
                        {[track.city, track.country].filter(Boolean).join(", ")}
                        {track.layouts && track.layouts.length > 0
                          ? ` · ${track.layouts.length} layout${track.layouts.length !== 1 ? "s" : ""}`
                          : ""}
                      </p>
                      {hint && (
                        <p className="text-xs text-amber-600 mt-0.5">{hint}</p>
                      )}
                    </div>
                    {track.layouts && track.layouts.length > 0 && (
                      <svg className="h-4 w-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </Modal>
      )}
    </>
  );
}
